import {
    ModuleAccessLevel,
    TimeTrialCalculationLevel,
    ValueMetricsProductType
} from '../../constants-types/Enums';
import { List } from '../../constants-types/PiList.model';

/**
 * ClassificationType composed on the SMAC response,
 * Account wide (not module specific) - properties or classifiers that might be useful: think Sales + Demo motions,
 * or any other general purpose Account Classifications that might arise
 *
 * This is a string enum and will always serialize out from the endpoint as a string instead of an enum number
 * The hope is that all enums going forward are serialized as strings instead of numbers which is very hard to reason about during debugging
 */
export enum ClassificationType {
    /**
     * The typical default if this value hasn't been set during the AP process or not been updated by systems
     */
    Unknown = 'Unknown',

    /**
     * Client Account: An actual account with data tied to a real paying client.
     */
    Client = 'Client',

    /**
     * Seller Account: An account granted to a partner firm (e.g., Founding Partner, Recruited Partner, Direct Sales, CSM) that
     * contains their prospect and business BAs and has seller-specific tools to spin up dynamic demo accounts and send prospect OI links
     */
    Seller = 'Seller',

    /**
     * Seller Granted Trial: A free trial account provisioned by Salesforce that upon creation contains a copy of the prospect's BAs and is
     * limited in time duration. Prospects will be granted access to this account and Salesforce will know about it's existence.
     */
    SellerGrantedTrial = 'SellerGrantedTrial',

    /**
     * Workshop Account: A temporary account requested by Salesforce that upon creation contains a copy of the workshop data set and is
     * limited in time duration. Users will log into this account via granted and enabled third party access and can access this account
     * from the account switcher in their primary account. Salesforce will not know about it's existence.
     */
    Workshop = 'Workshop',

    /**
     * Dynamic Demo Account: A temporary account requested by Salesforce that upon creation,
     * Contains a copy of a prospect's BA data as well as a stored sample data set.
     * The account will be short lived and will be cleaned up after a certain amount of inactivity.
     * Sellers will log into this account via granted and enabled third party access and can access this account
     * from the account switcher in their Seller account. Salesforce will not know about it's existence.
     * Prospects will not have login access to these accounts.
     */
    DynamicDemo = 'DynamicDemo',

    /**
     * Test Account: If it isn't obvious that it is a test account its probably not a test account:
     * Typically throwaway (fake-) or longer lived test accounts created via The PIT
     */
    Test = 'Test',

    /**
     * Legacy Demo Account: Any account 1.0 or 2.0 previously provisioned by Salesforce for the purposes of demoing the software. It stores
     * Sample Data and may have access to the Demo Mode toggle. These accounts will be sunset in the future.
     */
    LegacyDemoAccount = 'LegacyDemoAccount',

    /**
     * Legacy Business Account: Any account 1.0 partner prospecting and business account previously provisioned by Salesforce. It stores
     * real data and is the primary account for many of the Sellers who have 3rd party access to the new Seller accounts.
     * These accounts will be sunset in the future.
     */
    LegacyBusinessAccount = 'LegacyBusinessAccount'
}

export type AccountClassification = {
    classificationType: ClassificationType;
};

/**
 * The SoftwareModuleAccessConfiguration (SMAC) response currently composed of the SMAC document, ValueMetrics, TimeTrials
 * and AccountClassification, these documents don't have to and are specifically intended not to exist in the same backing
 * document and/or container in Cosmos DB - to keep concerns seperate and keep updates/modification clean.
 *
 * It is trivial to compose a much more useful response when reading,
 * but (deeply) nested documents with different concerns (domains) should be avoided if possible (in the write operation)
 */
export type SoftwareModuleAccessConfiguration = {
    softwareModuleAccessConfig: Array<ModuleAccessConfiguration>;
    accountClassification: AccountClassification;
};

export type ModuleAccessConfiguration = {
    name: ModuleName;
    accessLevel: ModuleAccessLevel;
    valueMetrics: List<ValueMetricLimitOld>;
    timeTrials: List<TimeTrial>;
    moduleExperienceType?: ModuleExperienceType;
    moduleAccountType?: ModuleAccountType;
};

/**
 * Used for passing {@link SoftwareModuleAccessConfiguration} to DataDog.
 *
 * Unlike SoftwareModuleAccessConfiguration, here each service name
 * exists as a key making it easier to filter RUM events.
 */
export type SoftwareModuleAccessRUMConfiguration = {
    softwareModuleAccessRUMConfig: {
        Hire?: ModuleAccessRUMConfiguration;
        Design?: ModuleAccessRUMConfiguration;
        Diagnose?: ModuleAccessRUMConfiguration;
        Inspire?: ModuleAccessRUMConfiguration;
    };
};

export type ModuleAccessRUMConfiguration = {
    accessLevel: ModuleAccessLevel;
    moduleExperienceType?: ModuleExperienceType;
    moduleAccountType?: ModuleAccountType;
};

export type TimeTrial = {
    timeTrialId: string; // guid
    calculationLevel: TimeTrialCalculationLevel;
    product: ValueMetricsProductType;
    startDate: string; // DateTime
    endDate: string; // DateTime
    durationDays: number;
    isExpired: boolean;
    associatedValueMetricName: string;
    associatedUserId: string; // Guid
    companyId: string; // Guid
    apiCompanyId: string;
};

export type ValueMetricLimitOld = {
    name: string;
    limit: number;
};

export enum ModuleName {
    Unknown = 'Unknown',
    Hire = 'Hire',
    Inspire = 'Inspire',
    Design = 'Design',
    Diagnose = 'Diagnose',
    HireHero = 'HireHero',
    Design2 = 'Design2',
    Diagnose2 = 'Diagnose2',
    Inspire2 = 'Inspire2',
    Perform = 'Perform'
}

export enum ModuleExperienceType {
    // Tied to server
    Unknown = 0,
    Classic = 1, // Standard UX Experience for Module,
    SelfServe = 2 // SelfServe Specific Experience for Module
}

export enum ModuleAccountType {
    // Tied to server
    Unknown = 0,
    Free = 1,
    Paid = 2
}
